import React, { useState, useEffect } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calculator, TrendingUp, TrendingDown, DollarSign, Users, Building, BarChart3, PieChart as PieChartIcon, FileText, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import { usePermissions } from "../contexts/PermissionsContext";

const COLORS = ['#3B82F6', '#F59E0B', '#8B5CF6', '#10B981', '#EF4444', '#6B7280'];

const JobCostingPage = () => {
  const { can } = usePermissions();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("overview");
  const [selectedProject, setSelectedProject] = useState("");
  const [costingData, setCostingData] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [clientSummary, setClientSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("simple"); // simple or detailed

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes] = await Promise.all([
        API.get("/projects"),
        API.get("/clients")
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      
      // Fetch company overview by default
      fetchOverview();
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const response = await API.get("/dashboard/company-summary");
      setOverviewData(response.data);
      setCostingData(null);
      setClientSummary(null);
    } catch (error) {
      console.error("Failed to load overview", error);
    }
  };

  const fetchClientSummary = async (clientId) => {
    try {
      const response = await API.get(`/dashboard/client-summary/${clientId}`);
      setClientSummary(response.data);
      setCostingData(null);
      setOverviewData(null);
    } catch (error) {
      toast.error("Failed to load client summary");
    }
  };

  const fetchCostingData = async (projectId) => {
    try {
      const response = await API.get(`/job-costing/${projectId}`);
      setCostingData(response.data);
      setOverviewData(null);
      setClientSummary(null);
    } catch (error) {
      toast.error("Failed to load job costing data");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClient === "overview") {
      fetchOverview();
      setSelectedProject("");
    } else if (selectedClient) {
      fetchClientSummary(selectedClient);
      setSelectedProject("");
    }
  }, [selectedClient]);

  useEffect(() => {
    if (selectedProject) {
      fetchCostingData(selectedProject);
    }
  }, [selectedProject]);

  // Get projects for selected client
  const clientProjects = selectedClient && selectedClient !== "overview"
    ? projects.filter(p => p.client_id === selectedClient)
    : projects;

  const categories = [
    { key: "labor", label: "Labor", color: "bg-blue-500" },
    { key: "materials", label: "Materials", color: "bg-amber-500" },
    { key: "subcontractors", label: "Subcontractors", color: "bg-purple-500" },
    { key: "equipment", label: "Equipment", color: "bg-emerald-500/100" },
    { key: "permits_fees", label: "Permits & Fees", color: "bg-red-500/100" },
    { key: "other", label: "Other", color: "bg-slate-8000" },
  ];

  const getVarianceClass = (budget, actual) => {
    const variance = budget - actual;
    if (variance < 0) return "text-red-600";
    if (variance > budget * 0.2) return "text-emerald-600";
    return "text-amber-600";
  };

  const fmt = (num) => new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0 
  }).format(num || 0);

  if (loading) {
    return <div className="animate-pulse">Loading job costing...</div>;
  }

  return (
    <div data-testid="job-costing-page" className="space-y-6 animate-slide-in">
      {/* Header with Filters */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Job Costing</h1>
          <p className="text-slate-500 font-ibm mt-1">Track budget vs actual costs</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode("simple")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === "simple" ? "bg-slate-700 shadow text-slate-200" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Simple
            </button>
            <button
              onClick={() => setViewMode("detailed")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === "detailed" ? "bg-slate-700 shadow text-slate-200" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Detailed
            </button>
          </div>
          
          {/* Client Filter */}
          <Select value={selectedClient} onValueChange={(v) => { setSelectedClient(v); setSelectedProject(""); }}>
            <SelectTrigger className="w-48" data-testid="client-select">
              <Users className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="overview">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-primary" />
                  <span className="font-medium">Overview (All)</span>
                </div>
              </SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.client_id} value={client.client_id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Project Filter - only show when client selected */}
          {selectedClient && selectedClient !== "overview" && (
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-56" data-testid="project-select">
                <Building className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_projects">All Projects</SelectItem>
                {clientProjects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* ============== OVERVIEW MODE ============== */}
      {selectedClient === "overview" && overviewData && (
        <>
          {/* Company Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <DollarSign size={18} />
                  <span className="text-sm font-medium">Total Budget</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-white">
                  {fmt(overviewData.total_budget)}
                </p>
                <p className="text-sm text-slate-500 mt-1">{overviewData.active_projects} active projects</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <TrendingDown size={18} />
                  <span className="text-sm font-medium">Total Spent</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-amber-600">
                  {fmt(overviewData.total_spent)}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {overviewData.total_budget > 0 ? Math.round((overviewData.total_spent / overviewData.total_budget) * 100) : 0}% of budget
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <TrendingUp size={18} />
                  <span className="text-sm font-medium">Total Collected</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-emerald-600">
                  {fmt(overviewData.total_collected)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Calculator size={18} />
                  <span className="text-sm font-medium">Outstanding AR</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-blue-600">
                  {fmt(overviewData.total_ar)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed View - Show all projects table */}
          {viewMode === "detailed" && projects.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>All Active Projects</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Project</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Client</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Budget</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Spent</th>
                        <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Progress</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Remaining</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {projects.filter(p => p.status === "active").map((project) => {
                        const budget = project.budget || 0;
                        const spent = 0; // Would need to fetch per-project costs
                        const percentage = budget > 0 ? (spent / budget) * 100 : 0;
                        const client = clients.find(c => c.client_id === project.client_id);
                        
                        return (
                          <tr key={project.project_id} className="hover:bg-slate-800">
                            <td className="px-4 py-3 font-medium text-white">{project.name}</td>
                            <td className="px-4 py-3 text-slate-400">{client?.name || "-"}</td>
                            <td className="px-4 py-3 text-right text-slate-400">{fmt(budget)}</td>
                            <td className="px-4 py-3 text-right text-white">{fmt(spent)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full ${percentage > 100 ? "bg-red-500/100" : percentage > 80 ? "bg-amber-500" : "bg-emerald-500/100"}`}
                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs text-slate-500 w-10">{percentage.toFixed(0)}%</span>
                              </div>
                            </td>
                            <td className={`px-4 py-3 text-right font-medium ${(budget - spent) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                              {fmt(budget - spent)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Projects at Risk */}
          {overviewData.projects_at_risk?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <TrendingDown className="h-5 w-5" />
                  Projects at Risk
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {overviewData.projects_at_risk.map((project) => (
                    <div key={project.project_id} className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg">
                      <div>
                        <p className="font-medium text-white">{project.project_name}</p>
                        <p className="text-sm text-slate-500">Budget: {fmt(project.budget)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-red-600">{fmt(project.spent)} spent</p>
                        <p className="text-sm text-red-500">{fmt(project.variance)} over</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming Bills */}
          {overviewData.upcoming_bills?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-amber-500" />
                  Upcoming Bills
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overviewData.upcoming_bills.map((bill) => (
                    <div key={bill.cost_id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                      <div>
                        <p className="font-medium text-white">{bill.vendor_name}</p>
                        <p className="text-sm text-slate-500">{bill.trade_phase}</p>
                      </div>
                      <p className="font-semibold">{fmt(bill.amount)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ============== CLIENT SUMMARY MODE ============== */}
      {clientSummary && !selectedProject && (
        <>
          {/* Client Summary Header */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-6">
            <h2 className="text-xl font-chivo font-bold text-white mb-1">
              {clientSummary.client_name}
            </h2>
            <p className="text-slate-400">{clientSummary.total_projects} project(s)</p>
          </div>

          {/* Client Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <DollarSign size={18} />
                  <span className="text-sm font-medium">Total Budget</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-white">
                  {fmt(clientSummary.total_budget)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <TrendingDown size={18} />
                  <span className="text-sm font-medium">Total Spent</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-amber-600">
                  {fmt(clientSummary.total_spent)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Calculator size={18} />
                  <span className="text-sm font-medium">Total Invoiced</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-blue-600">
                  {fmt(clientSummary.total_invoiced)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <TrendingUp size={18} />
                  <span className="text-sm font-medium">Total Collected</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-emerald-600">
                  {fmt(clientSummary.total_collected)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Client Projects Table */}
          <Card>
            <CardHeader>
              <CardTitle>Projects</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Project</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Budget</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Spent</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Invoiced</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Collected</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {clientSummary.projects?.map((project) => (
                      <tr 
                        key={project.project_id} 
                        className="hover:bg-slate-800 cursor-pointer"
                        onClick={() => setSelectedProject(project.project_id)}
                      >
                        <td className="px-4 py-3 font-medium text-white">{project.project_name}</td>
                        <td className="px-4 py-3">
                          <Badge variant={project.status === "active" ? "default" : "secondary"}>
                            {project.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right text-slate-400">{fmt(project.budget)}</td>
                        <td className="px-4 py-3 text-right text-white">{fmt(project.spent)}</td>
                        <td className="px-4 py-3 text-right text-blue-600">{fmt(project.invoiced)}</td>
                        <td className="px-4 py-3 text-right text-emerald-600">{fmt(project.collected)}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${project.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {fmt(project.profit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-800 border-t-2 border-slate-300">
                    <tr>
                      <td className="px-4 py-3 font-semibold text-white" colSpan={2}>Total</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmt(clientSummary.total_budget)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmt(clientSummary.total_spent)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-blue-600">{fmt(clientSummary.total_invoiced)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-600">{fmt(clientSummary.total_collected)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${(clientSummary.total_budget - clientSummary.total_spent) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {fmt(clientSummary.total_budget - clientSummary.total_spent)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ============== PROJECT DETAIL MODE ============== */}
      {costingData && selectedProject && (
        <>
          {/* Summary Cards - Always visible */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <DollarSign size={18} />
                  <span className="text-sm font-medium">Total Budget</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-white">
                  {fmt(costingData.budget)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <TrendingDown size={18} />
                  <span className="text-sm font-medium">Total Spent</span>
                </div>
                <p className="text-3xl font-chivo font-bold text-amber-600">
                  {fmt(costingData.total_actual)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <TrendingUp size={18} />
                  <span className="text-sm font-medium">Gross Profit</span>
                </div>
                <p className={`text-3xl font-chivo font-bold ${costingData.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {fmt(costingData.gross_profit)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Calculator size={18} />
                  <span className="text-sm font-medium">Gross Margin</span>
                </div>
                <p className={`text-3xl font-chivo font-bold ${costingData.gross_margin >= 20 ? "text-emerald-600" : costingData.gross_margin >= 0 ? "text-amber-600" : "text-red-600"}`}>
                  {costingData.gross_margin?.toFixed(1) || 0}%
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Simple View - Just summary with overall progress */}
          {viewMode === "simple" && (
            <Card>
              <CardHeader>
                <CardTitle>Budget vs Spent</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Overall Progress Bar */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-400">Overall Budget Usage</span>
                      <span className="text-sm text-slate-500">
                        {costingData.budget > 0 ? Math.round((costingData.total_actual / costingData.budget) * 100) : 0}%
                      </span>
                    </div>
                    <div className="h-4 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${(costingData.total_actual / costingData.budget) > 1 ? "bg-red-500/100" : (costingData.total_actual / costingData.budget) > 0.8 ? "bg-amber-500" : "bg-emerald-500/100"}`}
                        style={{ width: `${Math.min((costingData.total_actual / costingData.budget) * 100, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-sm text-slate-500">
                      <span>Spent: {fmt(costingData.total_actual)}</span>
                      <span>Budget: {fmt(costingData.budget)}</span>
                    </div>
                  </div>
                  
                  {/* Remaining / Over Budget */}
                  <div className={`p-4 rounded-lg ${costingData.gross_profit >= 0 ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${costingData.gross_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                        {costingData.gross_profit >= 0 ? "Remaining Budget" : "Over Budget"}
                      </span>
                      <span className={`text-2xl font-bold ${costingData.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {fmt(Math.abs(costingData.gross_profit))}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Detailed View - Full table with progress bars */}
          {viewMode === "detailed" && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Cost Breakdown by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-800 border-b">
                        <tr>
                          <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Budget</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actual</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Variance</th>
                          <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Progress</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {categories.map((cat) => {
                          const data = costingData.categories?.[cat.key] || { budget: 0, actual: 0 };
                          const variance = data.budget - data.actual;
                          const percentage = data.budget > 0 ? (data.actual / data.budget) * 100 : 0;

                          return (
                            <tr key={cat.key} data-testid={`category-${cat.key}`}>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className={`w-3 h-3 rounded-full ${cat.color}`} />
                                  <span className="font-medium text-white">{cat.label}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right text-slate-400">
                                {fmt(data.budget)}
                              </td>
                              <td className="px-6 py-4 text-right font-medium text-white">
                                {fmt(data.actual)}
                              </td>
                              <td className={`px-6 py-4 text-right font-semibold ${getVarianceClass(data.budget, data.actual)}`}>
                                {variance >= 0 ? "+" : ""}{fmt(variance)}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full ${percentage > 100 ? "bg-red-500/100" : percentage > 80 ? "bg-amber-500" : "bg-emerald-500/100"}`}
                                      style={{ width: `${Math.min(percentage, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-slate-500 w-12 text-right">
                                    {percentage.toFixed(0)}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-800 border-t-2 border-slate-300">
                        <tr>
                          <td className="px-6 py-4 font-semibold text-white">Total</td>
                          <td className="px-6 py-4 text-right font-semibold text-white">
                            {fmt(costingData.budget)}
                          </td>
                          <td className="px-6 py-4 text-right font-semibold text-white">
                            {fmt(costingData.total_actual)}
                          </td>
                          <td className={`px-6 py-4 text-right font-bold ${costingData.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {costingData.gross_profit >= 0 ? "+" : ""}{fmt(costingData.gross_profit)}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`font-semibold ${costingData.gross_margin >= 20 ? "text-emerald-600" : "text-amber-600"}`}>
                              {costingData.gross_margin?.toFixed(1)}% margin
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Budget vs Actual Bar Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Budget vs Actual by Category
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={categories.map(cat => ({
                          name: cat.label,
                          Budget: costingData.categories?.[cat.key]?.budget || 0,
                          Actual: costingData.categories?.[cat.key]?.actual || 0,
                        }))}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#94a3b8" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                          formatter={(value) => `$${value.toLocaleString()}`}
                        />
                        <Legend />
                        <Bar dataKey="Budget" fill="#3B82F6" />
                        <Bar dataKey="Actual" fill="#F59E0B" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Cost Distribution Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChartIcon className="h-5 w-5" />
                      Actual Cost Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={categories.map((cat, idx) => ({
                            name: cat.label,
                            value: costingData.categories?.[cat.key]?.actual || 0,
                          })).filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {categories.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                          formatter={(value) => `$${value.toLocaleString()}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Profitability Summary */}
              <Card className="bg-gradient-to-r from-slate-900 to-slate-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-emerald-500" />
                    Profitability Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                      <p className="text-sm text-slate-400 mb-1">Contract Value</p>
                      <p className="text-2xl font-bold text-white">{fmt(costingData.budget)}</p>
                    </div>
                    <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                      <p className="text-sm text-slate-400 mb-1">Total Costs</p>
                      <p className="text-2xl font-bold text-amber-500">{fmt(costingData.total_actual)}</p>
                    </div>
                    <div className={`text-center p-4 rounded-lg ${costingData.gross_profit >= 0 ? 'bg-emerald-900/30 border border-emerald-700/50' : 'bg-red-900/30 border border-red-700/50'}`}>
                      <p className="text-sm text-slate-400 mb-1">Gross Profit</p>
                      <p className={`text-2xl font-bold ${costingData.gross_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {fmt(costingData.gross_profit)}
                      </p>
                      <p className={`text-sm ${costingData.gross_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {costingData.gross_margin?.toFixed(1)}% margin
                      </p>
                    </div>
                  </div>
                  
                  {/* Profit Progress Bar */}
                  <div className="mt-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Budget Utilization</span>
                      <span className={costingData.total_actual > costingData.budget ? 'text-red-400' : 'text-emerald-400'}>
                        {costingData.budget > 0 ? ((costingData.total_actual / costingData.budget) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${costingData.total_actual > costingData.budget ? 'bg-red-500' : costingData.total_actual > costingData.budget * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${Math.min((costingData.total_actual / costingData.budget) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Budget Allocation Visual */}
              <Card>
                <CardHeader>
                  <CardTitle>Budget Allocation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex h-8 rounded-lg overflow-hidden">
                    {categories.map((cat) => {
                      const budget = costingData.categories?.[cat.key]?.budget || 0;
                      const percentage = costingData.budget > 0 ? (budget / costingData.budget) * 100 : 0;
                      if (percentage < 1) return null;
                      return (
                        <div
                          key={cat.key}
                          className={`${cat.color} relative group`}
                          style={{ width: `${percentage}%` }}
                        >
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="text-white text-xs font-medium drop-shadow">{cat.label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-4">
                    {categories.map((cat) => {
                      const budget = costingData.categories?.[cat.key]?.budget || 0;
                      const percentage = costingData.budget > 0 ? (budget / costingData.budget) * 100 : 0;
                      if (percentage < 1) return null;
                      return (
                        <div key={cat.key} className="flex items-center gap-2 text-sm">
                          <div className={`w-3 h-3 rounded-full ${cat.color}`} />
                          <span className="text-slate-400">{cat.label}: {percentage.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* No Selection State */}
      {!overviewData && !clientSummary && !costingData && (
        <Card>
          <CardContent className="py-12 text-center">
            <Calculator size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Select a view</h3>
            <p className="text-slate-500">Choose Overview, a client, or a project to view job costing</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JobCostingPage;
